import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ContactDetailsTab = ({ contact, onSave, isEditing, onToggleEdit }) => {
  const [editedContact, setEditedContact] = useState(contact);
  const [isSaving, setIsSaving] = useState(false);

  const handleInputChange = (field, value) => {
    setEditedContact(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(editedContact);
      onToggleEdit();
    } catch (error) {
      console.error('Error saving contact:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedContact(contact);
    onToggleEdit();
  };

  const contactFields = [
    {
      label: 'Full Name',
      field: 'name',
      type: 'text',
      icon: 'User',
      required: true
    },
    {
      label: 'Email Address',
      field: 'email',
      type: 'email',
      icon: 'Mail'
    },
    {
      label: 'Phone Number',
      field: 'phone',
      type: 'tel',
      icon: 'Phone'
    },
    {
      label: 'Company',
      field: 'company',
      type: 'text',
      icon: 'Building'
    },
    {
      label: 'Job Title',
      field: 'jobTitle',
      type: 'text',
      icon: 'Briefcase'
    },
    {
      label: 'Birthday',
      field: 'birthday',
      type: 'date',
      icon: 'Calendar'
    },
    {
      label: 'Location',
      field: 'location',
      type: 'text',
      icon: 'MapPin'
    },
    {
      label: 'Website',
      field: 'website',
      type: 'url',
      icon: 'Globe'
    }
  ];

  return (
    <div className="p-6">
      {/* Edit Mode Toggle */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-text-primary">Contact Information</h3>
        
        {!isEditing ? (
          <Button
            variant="outline"
            size="sm"
            onClick={onToggleEdit}
            iconName="Edit"
            iconPosition="left"
          >
            Edit Details
          </Button>
        ) : (
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleSave}
              loading={isSaving}
              iconName="Save"
              iconPosition="left"
            >
              Save Changes
            </Button>
          </div>
        )}
      </div>
      {/* Contact Fields */}
      <div className="space-y-6">
        {contactFields?.map((field) => (
          <div key={field?.field} className="group">
            {isEditing ? (
              <Input
                label={field?.label}
                type={field?.type}
                value={editedContact?.[field?.field] || ''}
                onChange={(e) => handleInputChange(field?.field, e?.target?.value)}
                required={field?.required}
                placeholder={`Enter ${field?.label?.toLowerCase()}`}
              />
            ) : (
              <div className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-micro">
                <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                  <Icon name={field?.icon} size={18} className="text-text-secondary" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    {field?.label}
                  </label>
                  
                  {contact?.[field?.field] ? (
                    <div className="text-text-primary">
                      {field?.type === 'date' ? (
                        new Date(contact[field.field])?.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })
                      ) : field?.type === 'email' ? (
                        <a 
                          href={`mailto:${contact?.[field?.field]}`}
                          className="text-primary hover:underline"
                        >
                          {contact?.[field?.field]}
                        </a>
                      ) : field?.type === 'tel' ? (
                        <a 
                          href={`tel:${contact?.[field?.field]}`}
                          className="text-primary hover:underline"
                        >
                          {contact?.[field?.field]}
                        </a>
                      ) : field?.type === 'url' ? (
                        <a 
                          href={contact?.[field?.field]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {contact?.[field?.field]}
                        </a>
                      ) : (
                        contact?.[field?.field]
                      )}
                    </div>
                  ) : (
                    <div className="text-text-secondary italic">
                      Not provided
                    </div>
                  )}
                </div>

                {/* Quick Action Buttons */}
                {!isEditing && contact?.[field?.field] && (
                  <div className="opacity-0 group-hover:opacity-100 transition-micro">
                    {field?.type === 'email' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => window.open(`mailto:${contact?.[field?.field]}`)}
                        className="h-8 w-8"
                      >
                        <Icon name="ExternalLink" size={14} />
                      </Button>
                    )}
                    {field?.type === 'tel' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => window.open(`tel:${contact?.[field?.field]}`)}
                        className="h-8 w-8"
                      >
                        <Icon name="ExternalLink" size={14} />
                      </Button>
                    )}
                    {field?.type === 'url' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => window.open(contact?.[field?.field], '_blank')}
                        className="h-8 w-8"
                      >
                        <Icon name="ExternalLink" size={14} />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Additional Information */}
      <div className="mt-8 pt-6 border-t border-border">
        <h4 className="text-md font-semibold text-text-primary mb-4">Additional Information</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="flex items-center space-x-2 text-text-secondary">
            <Icon name="Calendar" size={16} />
            <span>Added: {new Date(contact.createdAt || Date.now())?.toLocaleDateString()}</span>
          </div>
          
          <div className="flex items-center space-x-2 text-text-secondary">
            <Icon name="Clock" size={16} />
            <span>Last updated: {new Date(contact.updatedAt || Date.now())?.toLocaleDateString()}</span>
          </div>
          
          <div className="flex items-center space-x-2 text-text-secondary">
            <Icon name="MessageSquare" size={16} />
            <span>Messages sent: {contact?.messageCount || 0}</span>
          </div>
          
          <div className="flex items-center space-x-2 text-text-secondary">
            <Icon name="Eye" size={16} />
            <span>Profile views: {contact?.viewCount || 1}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactDetailsTab;